import type {
  ContentStep,
  PlanContent,
  UserPlans,
} from '@cfa/pathway-fe-shared/xp';

import Constants from '@/constants';

import type { PlanResourceDebugMockScenario } from '@/atoms/planResourceDebugMock';

const applyScenarioToStep = (
  step: ContentStep,
  scenario: PlanResourceDebugMockScenario,
): ContentStep => {
  // Callers only ever select document steps; `available` and `resource` exist
  // on no other member of the union.
  if (step.type !== Constants.STEP_TYPES.DOCUMENT) {
    return step;
  }

  switch (scenario) {
    case 'unavailable':
      return {
        ...step,
        available: false,
        resource: undefined,
      };
    case 'unavailable_with_ids':
      return {
        ...step,
        available: false,
      };
    case 'missing_resource_id':
      return {
        ...step,
        available: true,
        resource: step.resource
          ? { ...step.resource, id: '' }
          : {
              id: '',
              name: step.name,
              references: [],
            },
      };
    case 'missing_reference':
      return {
        ...step,
        available: true,
        resource: step.resource
          ? { ...step.resource, references: [] }
          : {
              id: 'debug-resource-id',
              name: step.name,
              references: [],
            },
      };
    case 'missing_both':
      return {
        ...step,
        available: true,
        resource: undefined,
      };
  }
};

const patchFirstDocumentStep = (
  content: PlanContent,
  scenario: PlanResourceDebugMockScenario,
): PlanContent => {
  const topLevelSteps = content.steps ?? [];
  for (let index = 0; index < topLevelSteps.length; index++) {
    const step = topLevelSteps[index];
    if (step?.type === Constants.STEP_TYPES.DOCUMENT) {
      return {
        ...content,
        steps: topLevelSteps.map((currentStep, stepIndex) =>
          stepIndex === index
            ? applyScenarioToStep(currentStep, scenario)
            : currentStep,
        ),
      };
    }
  }

  const sections = content.sections ?? [];
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const sectionSteps = sections[sectionIndex]?.steps ?? [];
    for (let stepIndex = 0; stepIndex < sectionSteps.length; stepIndex++) {
      const step = sectionSteps[stepIndex];
      if (step?.type === Constants.STEP_TYPES.DOCUMENT) {
        return {
          ...content,
          sections: sections.map((section, currentSectionIndex) =>
            currentSectionIndex === sectionIndex
              ? {
                  ...section,
                  steps: sectionSteps.map((currentStep, currentStepIndex) =>
                    currentStepIndex === stepIndex
                      ? applyScenarioToStep(currentStep, scenario)
                      : currentStep,
                  ),
                }
              : section,
          ),
        };
      }
    }
  }

  return content;
};

/** Dev-only: corrupt the first document step in a plan for resource_not_found testing. */
export const applyPlanResourceDebugMock = (
  planData: UserPlans,
  scenario: PlanResourceDebugMockScenario,
): UserPlans => {
  if (!planData.plan?.content) {
    return planData;
  }

  return {
    ...planData,
    plan: {
      ...planData.plan,
      content: patchFirstDocumentStep(planData.plan.content, scenario),
    },
  };
};
